import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { getBlogPostBySlug, getAllBlogPosts, getRelatedPosts } from '../../utils/enhancedBlogLoader';
import type { BlogPost } from '../../utils/blogTypes';
import { MDXProvider } from '@mdx-js/react';
import { mdxComponents } from '../../components/blog/mdxComponents';
import MarketingLayout from '../../components/marketing/MarketingLayout';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import BlogChart from '../../components/blog/BlogChart';
import BlogTable from '../../components/blog/BlogTable';
import InvestmentComparisonChart from '../../components/blog/InvestmentComparisonChart';
import RespGrowthChart from '../../components/blog/RespGrowthChart';
import ParWlGrowthChart from '../../components/blog/ParWlGrowthChart';
import EducationFundingComparisonChart from '../../components/blog/EducationFundingComparisonChart';
import RespVsWlQuickVisual from '../../components/blog/RespVsWlQuickVisual';
import { Card, Title, Text, Callout, Bold, Italic } from '@tremor/react';
import { LightBulbIcon, InformationCircleIcon, SparklesIcon } from '@heroicons/react/24/solid';

export default function BlogPostPage() {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();

  const [post, setPost] = useState<BlogPost | null>(null);
  const [allPosts, setAllPosts] = useState<BlogPost[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    async function loadBlogData() {
      try {
        setLoading(true);

        // Load the specific blog post
        if (slug) {
          const { post: postData, error: postError } = await getBlogPostBySlug(slug);
          
          if (postError) {
            setError(postError.message);
            return;
          }
          
          if (postData) {
            setPost(postData);
            
            // Load related posts using the enhanced helper
            const relatedPosts = await getRelatedPosts(postData, 3);
            setAllPosts(relatedPosts);
          } else {
            setError('Blog post not found');
          }
        }
      } catch (err) {
        if (import.meta.env.DEV) {

          if (import.meta.env.DEV) {
            console.error('Error loading blog post:', err);
          }

        }
        setError('Failed to load blog post. Please try again later.');
      } finally {
        setLoading(false);
      }
    }

    loadBlogData();
  }, [slug]);

  if (loading) {
    return (
      <MarketingLayout>
        <div className="max-w-2xl mx-auto py-16 px-4 text-center">
          <h1 className="text-2xl font-bold mb-4">Loading...</h1>
          <div className="animate-pulse h-4 bg-gray-700 rounded w-32 mx-auto"></div>
        </div>
      </MarketingLayout>
    );
  }

  if (error) {
    return (
      <MarketingLayout>
        <div className="max-w-2xl mx-auto py-16 px-4 text-center">
          <h1 className="text-2xl font-bold mb-4">Error</h1>
          <p className="mb-4">{error}</p>
          <button className="btn-primary" onClick={() => navigate(-1)}>Go Back</button>
        </div>
      </MarketingLayout>
    );
  }

  if (!post) {
    return (
      <MarketingLayout>
        <div className="max-w-2xl mx-auto py-16 px-4 text-center">
          <h1 className="text-2xl font-bold mb-4">Post Not Found</h1>
          <button className="btn-primary" onClick={() => navigate(-1)}>Go Back</button>
        </div>
      </MarketingLayout>
    );
  }

  const MdxContent = post.Component;
  const hasComponent = MdxContent !== null && MdxContent !== undefined;

  // Related posts: same category or overlapping tags, not self
  const related = allPosts.filter((p) =>
    p.slug !== post.slug &&
    (p.category === post.category || p.tags.some((tag: string) => post.tags.includes(tag)))
  ).slice(0, 3);

  return (
    <MarketingLayout>
      <div className="min-h-screen bg-gray-100 py-12 px-3" style={{ backgroundColor: '#f3f4f6' }}>
        <article className="max-w-4xl mx-auto">
          {/* Paper-like container */}
          <div className="bg-white text-gray-800 rounded-lg shadow-lg p-8 mb-8" style={{ backgroundColor: 'white', color: '#1f2937' }}>
          <div className="mb-6">
            <Link to="/blog" className="text-blue-600 hover:text-blue-800 text-xs">← Back to Blog</Link>
          </div>
          <h1 className="text-3xl font-bold mb-3 text-gray-900">{post.title}</h1>
          <div className="flex flex-wrap gap-2 mb-2">
            <span className="bg-blue-700 text-xs text-white px-2 py-0.5 rounded-full">{post.category}</span>
            {post.tags.map(tag => (
              <span key={tag} className="bg-blue-100 text-xs text-blue-800 px-2 py-0.5 rounded-full">{tag}</span>
            ))}
          </div>
          <div className="text-gray-600 text-sm mb-4">{post.excerpt}</div>
          
          {/* Visual Elements - Moved to top */}
          {/* TFSA vs RRSP Infographic */}
          {post.slug === 'tfsa-vs-rrsp-2025' && (
            <div className="mb-8">
              {/* Add the advanced investment comparison chart */}
              <InvestmentComparisonChart />
            </div>
          )}

          {/* Farm Succession Planning Process Visual */}
          {post.slug === 'farm-succession-canada-2025' && (
            <div className="mb-8">
              <Card className="border border-green-100">
                <Title className="text-green-800">Farm Succession Planning Process</Title>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-4">
                  <div className="p-4 bg-green-50 rounded-lg text-center">
                    <div className="text-green-700 font-bold text-xl mb-2">Phase 1</div>
                    <div className="text-gray-700">Family Communication & Goal Setting</div>
                  </div>
                  <div className="p-4 bg-green-50 rounded-lg text-center">
                    <div className="text-green-700 font-bold text-xl mb-2">Phase 2</div>
                    <div className="text-gray-700">Financial & Legal Structure Design</div>
                  </div>
                  <div className="p-4 bg-green-50 rounded-lg text-center">
                    <div className="text-green-700 font-bold text-xl mb-2">Phase 3</div>
                    <div className="text-gray-700">Implementation & Transition Management</div>
                  </div>
                </div>
              </Card>
            </div>
          )}

          {/* RESP vs. Whole Life Insurance Visual Elements */}
          {post.slug === 'resp-vs-whole-life-insurance-canada' && (
            <div className="mb-8">
              {/* Quick Visual at the top */}
              <div className="mb-6">
                <RespVsWlQuickVisual />
              </div>
              
              {/* RESP Growth Chart */}
              <RespGrowthChart />
              
              {/* PAR WL Growth Chart */}
              <ParWlGrowthChart />
              
              {/* Strategy Comparison Chart */}
              <EducationFundingComparisonChart />
            </div>
          )}

          {/* Corporate-Owned Life Insurance Visual */}
          {post.slug === 'corporate-owned-life-insurance-canada' && (
            <div className="mb-8">
              <Card className="border border-blue-100">
                <Title className="text-blue-800">Strategic Uses of Corporate-Owned Life Insurance</Title>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
                  <div className="p-4 bg-blue-50 rounded-lg">
                    <div className="text-blue-700 font-bold mb-2">Buy-Sell Funding</div>
                    <div className="text-gray-700 text-sm">Provides immediate liquidity to fund share purchase agreements when a shareholder dies</div>
                  </div>
                  <div className="p-4 bg-blue-50 rounded-lg">
                    <div className="text-blue-700 font-bold mb-2">Key Person Protection</div>
                    <div className="text-gray-700 text-sm">Mitigates financial impact from the loss of critical talent or leadership</div>
                  </div>
                  <div className="p-4 bg-blue-50 rounded-lg">
                    <div className="text-blue-700 font-bold mb-2">Tax-Efficient Wealth Transfer</div>
                    <div className="text-gray-700 text-sm">Creates a mechanism for transferring corporate value to the next generation with minimal taxation</div>
                  </div>
                  <div className="p-4 bg-blue-50 rounded-lg">
                    <div className="text-blue-700 font-bold mb-2">Retirement Planning</div>
                    <div className="text-gray-700 text-sm">Cash value can supplement retirement income through policy loans or withdrawals</div>
                  </div>
                </div>
              </Card>
            </div>
          )}
          
          <div className="prose prose-lg prose-gray max-w-none mb-8">
            {hasComponent ? (
              <MDXProvider components={mdxComponents}>
                <MdxContent />
              </MDXProvider>
            ) : (
              <ReactMarkdown 
                remarkPlugins={[remarkGfm]}
                components={{
                  h1: ({node, ...props}) => <h1 className="text-3xl font-bold mb-4 text-gray-900" style={{ color: '#111827' }} {...props} />,
                  h2: ({node, ...props}) => <h2 className="text-2xl font-bold mb-3 text-gray-800" style={{ color: '#1f2937' }} {...props} />,
                  h3: ({node, ...props}) => <h3 className="text-xl font-semibold mb-2 text-gray-700" style={{ color: '#374151' }} {...props} />,
                  p: ({node, ...props}) => <p className="mb-4 text-gray-700 leading-relaxed" style={{ color: '#374151' }} {...props} />,
                  ul: ({node, ...props}) => <ul className="mb-4 list-disc list-inside space-y-1" {...props} />,
                  ol: ({node, ...props}) => <ol className="mb-4 list-decimal list-inside space-y-1" {...props} />,
                  li: ({node, ...props}) => <li className="text-gray-700" {...props} />,
                  strong: ({node, ...props}) => <strong className="font-semibold text-gray-900" {...props} />,
                  em: ({node, ...props}) => <em className="italic" {...props} />,
                  blockquote: ({node, ...props}) => <blockquote className="border-l-4 border-blue-500 pl-4 my-4 italic text-gray-700" {...props} />,
                  a: ({node, ...props}) => <a className="text-blue-600 hover:text-blue-800 underline" {...props} />,
                  code: ({node, inline, ...props}) => inline ? 
                    <code className="bg-gray-100 px-1 py-0.5 rounded text-sm" {...props} /> :
                    <code className="block bg-gray-100 p-4 rounded-lg overflow-x-auto" {...props} />,
                  table: ({node, ...props}) => <table className="min-w-full border-collapse border border-gray-300" {...props} />,
                  th: ({node, ...props}) => <th className="border border-gray-300 px-4 py-2 bg-gray-100 font-semibold" {...props} />,
                  td: ({node, ...props}) => <td className="border border-gray-300 px-4 py-2" {...props} />,
                }}
              >
                {post.content || ''}
              </ReactMarkdown>
            )}
          </div>
          {/* Add special callouts for key information */}
          {/* Custom callouts based on blog post slug */}
          {post.slug === 'tfsa-vs-rrsp-2025' && (
            <>
              <Callout
                title="Key Consideration: Tax Brackets Matter"
                icon={LightBulbIcon}
                color="amber"
                className="my-6 border border-amber-200 bg-amber-50"
              >
                <Text className="text-gray-700">
                  The optimal choice between TFSA and RRSP often depends on your current vs. future tax brackets.
                  If you expect to be in a <Bold className="text-amber-700">lower</Bold> tax bracket in retirement, an RRSP may be more advantageous.
                  If you expect to be in a <Bold className="text-amber-700">higher</Bold> tax bracket in retirement, a TFSA is likely better.
                </Text>
              </Callout>

              <Callout
                title="Expert Tip: Consider a Balanced Approach"
                icon={SparklesIcon}
                color="blue"
                className="my-6 border border-blue-200 bg-blue-50"
              >
                <Text className="text-gray-700">
                  For many Canadians, the ideal strategy is a combination of both accounts.
                  Use your TFSA for emergency funds and short-term goals, while maximizing RRSP contributions
                  during your highest-earning years to benefit from tax deferrals.
                </Text>
              </Callout>
            </>
          )}

          {/* Farm Succession Blog Post Callouts */}
          {post.slug === 'farm-succession-canada-2025' && (
            <>
              <Callout
                title="Key Insight: Start Planning Early"
                icon={LightBulbIcon}
                color="green"
                className="my-6 border border-green-200 bg-green-50"
              >
                <Text className="text-gray-700">
                  The most successful farm succession plans begin <Bold className="text-green-700">5-10 years</Bold> before the intended transition.
                  Early planning provides time to address tax implications, train successors, and implement
                  the right legal structures for your unique situation.
                </Text>
              </Callout>

              <Callout
                title="Tax Strategy Alert"
                icon={SparklesIcon}
                color="amber"
                className="my-6 border border-amber-200 bg-amber-50"
              >
                <Text className="text-gray-700">
                  The Lifetime Capital Gains Exemption (LCGE) of <Bold className="text-amber-700">$1,000,000</Bold> for qualified farm property
                  is a powerful tax-saving tool. Proper structuring can allow multiple family members to
                  utilize their exemptions, potentially saving hundreds of thousands in taxes.
                </Text>
              </Callout>
            </>
          )}

          {/* RESP vs. Whole Life Insurance Blog Post Callouts */}
          {post.slug === 'resp-vs-whole-life-insurance-canada' && (
            <>
              <Callout
                title="Key Consideration: Start Early"
                icon={LightBulbIcon}
                color="blue"
                className="my-6 border border-blue-200 bg-blue-50"
              >
                <Text className="text-gray-700">
                  The power of compounding makes starting early crucial for both RESPs and Whole Life policies.
                  Every year you delay can significantly reduce the final value. For RESPs, starting early also
                  maximizes the total government grants you can receive over time.
                </Text>
              </Callout>

              <Callout
                title="Expert Tip: Consider Your Family's Unique Needs"
                icon={SparklesIcon}
                color="purple"
                className="my-6 border border-purple-200 bg-purple-50"
              >
                <Text className="text-gray-700">
                  While RESPs offer excellent education-specific benefits through government grants,
                  PAR WL insurance provides unmatched flexibility and lifelong advantages. For many families,
                  a balanced approach capturing the strengths of both may be optimal.
                </Text>
              </Callout>
            </>
          )}

          {/* Corporate-Owned Life Insurance Blog Post Callouts */}
          {post.slug === 'corporate-owned-life-insurance-canada' && (
            <>
              <Callout
                title="Tax Advantage: Capital Dividend Account"
                icon={LightBulbIcon}
                color="blue"
                className="my-6 border border-amber-200 bg-amber-50"
              >
                <Text className="text-gray-700">
                  When insurance proceeds are paid to a corporation, the excess of the death benefit over the policy's
                  Adjusted Cost Basis creates a <Bold className="text-blue-700">tax-free credit</Bold> to the Capital Dividend Account.
                  This allows for tax-free distributions to shareholders—a significant advantage over personally-owned policies.
                </Text>
              </Callout>

              <Callout
                title="Implementation Consideration"
                icon={SparklesIcon}
                color="purple"
                className="my-6 border border-purple-200 bg-purple-50"
              >
                <Text className="text-gray-700">
                  The structure of a corporate-owned policy is critical. Careful consideration must be given to
                  who owns the policy, who is insured, and who is the beneficiary. These decisions have significant
                  tax and legal implications that can affect the ultimate benefits received.
                </Text>
              </Callout>
            </>
          )}

          {/* Render charts if available */}
          {post.charts && post.charts.length > 0 && (
            <div className="my-8">
              {post.charts.map((chart, index) => (
                <BlogChart key={index} chartData={chart} />
              ))}
            </div>
          )}

          {/* Render tables if available */}
          {post.tables && post.tables.length > 0 && (
            <div className="my-8">
              {post.tables.map((table, index) => (
                <BlogTable key={index} tableData={table} />
              ))}
            </div>
          )}

          {/* Add compliance note in a styled callout */}
          {post.compliance && (
            <Callout
              title="Important Disclosure"
              icon={InformationCircleIcon}
              color="gray"
              className="mt-8 border border-gray-200 bg-gray-50"
            >
              <Text className="text-xs text-gray-600">{post.compliance}</Text>
            </Callout>
          )}
        </div> {/* Close the paper-like container div */}

        {post.cta && (
          <div className="my-8 text-center">
            {post.cta.startsWith('/') ? (
              <Link to={post.cta} className="inline-block bg-brand-primary hover:bg-brand-accent text-white font-bold py-3 px-8 rounded-lg transition-all">Book Your Consultation</Link>
            ) : (
              <a href={post.cta} className="inline-block bg-brand-primary hover:bg-brand-accent text-white font-bold py-3 px-8 rounded-lg transition-all" target="_blank" rel="noopener noreferrer">Book Your Consultation</a>
            )}
          </div>
        )}
        </article> {/* Close the article tag */}

        {/* Related posts */}
        {related.length > 0 && (
          <div className="bg-white text-gray-800 rounded-lg shadow-lg p-8 mb-8">
            <h2 className="text-xl font-bold mb-4 text-blue-800 border-b border-blue-100 pb-2">Related Posts</h2>
            <div className="space-y-4">
              {related.map(rp => (
                <Link key={rp.slug} to={`/blog/${rp.slug}`} className="block bg-white p-4 rounded-lg border border-gray-200 shadow-sm hover:shadow-md transition-all">
                  <div className="font-semibold text-blue-800">{rp.title}</div>
                  <div className="text-gray-600 text-xs mt-1">{rp.excerpt}</div>
                </Link>
              ))}
            </div>
          </div>
        )}
      </div> {/* Close the min-h-screen div */}
    </MarketingLayout>
  );
}
